import { useCallback, useRef } from 'react';
import type { Result } from '../types/result';
import { useSiteConfig } from '../providers/ExperienceConfigProvider';
import { useDynamicExperienceContext } from '../providers/DynamicExperienceProvider';
import { ensureConversion } from '../utils/ensureAppViewConversion';
import { readStepMonetizationChannel } from '../utils/configurable-step';

/**
 * Create-on-click seam for the direct external-application `window.open`
 * surfaces — a submitted result `Card` (start-your-application) and the
 * Recommender `appCta` — that route the user into an application view without
 * passing through the governed lead-flow submit path.
 *
 * Fires the CMS "APP_VIEWED" conversion (`ensureConversion`) at click time for
 * Supersonic parity, deduped **once per selection** so re-clicking the same
 * card does not double-create. The dedup key follows the card's
 * `productClickedCorrelationId` (stable per card instance), falling back to the
 * offering / program identity.
 *
 * Fire-and-forget: the create runs in the background so the click handler can
 * proceed to open the application view immediately.
 */
export const useAppViewConversion = (): {
  createAppViewConversion: (result: Result) => void;
} => {
  const siteConfig = useSiteConfig();
  const { flowState, currentStep } = useDynamicExperienceContext();
  const seenRef = useRef<Set<string>>(new Set());

  const createAppViewConversion = useCallback(
    (result: Result): void => {
      const key =
        result.productClickedCorrelationId ||
        result.offeringId ||
        result.offeringUuid ||
        String(result.programId);
      if (seenRef.current.has(key)) return;
      // Mark seen before the async create resolves so a rapid double-click can't
      // race two creates through. Trade-off: a failed create is not retried on
      // re-click — acceptable under the fail-safe intent (a dropped click-time
      // conversion still gets created by the governed submit path).
      seenRef.current.add(key);

      // Same `flowState.location` merge point the submit path reads (IP
      // inference, overwritten by an answered location question) — carries
      // the visitor's real geo into this click-time create instead of letting
      // CMS fall back to its viewer-country headers for this server-proxied
      // call.
      const locationState = flowState?.['location'] as
        | { countryCode?: string; state?: string }
        | undefined;

      void ensureConversion(
        result,
        siteConfig,
        result.productClickedCorrelationId || '',
        undefined,
        {
          country: locationState?.countryCode,
          state: locationState?.state,
        },
        undefined,
        readStepMonetizationChannel(currentStep),
      );
    },
    [siteConfig, flowState, currentStep],
  );

  return { createAppViewConversion };
};
